import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import './ResultsChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ResultsChart = ({ slideData, results, slideType }) => {
  if (!slideData || !results || !slideData.options) {
    return (
      <div className="results-chart-container">
        <div className="no-data">
          <p className="text-[1vw]">No data available for this slide</p>
        </div>
      </div>
    );
  }

  const { options } = slideData;
  const { optionCounts, percentages, totalResponses } = results;

  // Calculate viewport-based font sizes
  const getVwFontSize = (vwValue) => {
    return Math.max(8, (window.innerWidth * vwValue) / 100);
  };

  // Prepare data for charts
  const labels = options.map(opt => (typeof opt === 'object' ? opt.choice_text : opt));
  const data = options.map(option => {
    const key = typeof option === 'object' ? option.id : option;
    return optionCounts[key] || 0;
  });
  const backgroundColors = [
    '#FF6384',
    '#36A2EB',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
    '#FF6384',
    '#C9CBCF'
  ];

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Responses',
        data: data,
        backgroundColor: backgroundColors.slice(0, labels.length),
        borderColor: backgroundColors.slice(0, labels.length).map(color => color + '80'),
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: getVwFontSize(0.8)
          }
        }
      },
      title: {
        display: true,
        text: `${slideData.question} (${totalResponses} responses)`,
        font: {
          size: getVwFontSize(0.6)
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed.y;
            
            const index = context.dataIndex;
            const option = options[index];
            const key = typeof option === 'object' ? option.id : option;
            const percentage = percentages[key] || 0;
            
            return `${label}: ${value} (${percentage}%)`;
          }
        },
        titleFont: {
          size: getVwFontSize(0.2)
        },
        bodyFont: {
          size: getVwFontSize(0.2)
        }
      }
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: getVwFontSize(0.2)
          }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: {
            size: getVwFontSize(0.2)
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: {
            size: getVwFontSize(0.9)
          }
        }
      },
      title: {
        display: true,
        text: `${slideData.question} (${totalResponses} responses)`,
        font: {
          size: getVwFontSize(0.8)
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            
            const index = context.dataIndex;
            const option = options[index];
            const key = typeof option === 'object' ? option.id : option;
            const percentage = percentages[key] || 0;
            
            return `${label}: ${value} (${percentage}%)`;
          }
        },
        titleFont: {
          size: getVwFontSize(0.6)
        },
        bodyFont: {
          size: getVwFontSize(0.6)
        }
      }
    }
  };

  return (
    <div className="results-chart-container">
      <div className="chart-header">
        <h3 className="text-[1vw]">Live Results</h3>
        <div className="response-count">
          Total Responses: <strong className="text-[1vw]">{totalResponses}</strong>
        </div>
      </div>

      <div className="">
        {/* <div className="chart-wrapper">
          <h4 className="text-[1vw]">Bar Chart</h4>
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div> */}

        <div className="chart-wrapper">
          <h4 className="text-[1vw]">Pie Chart</h4>
          <div className="chart-container">
            <Doughnut data={chartData} options={doughnutOptions} />
          </div>
        </div>
      </div>

      <div className="results-table">
        <h4 className="text-[1vw]">Detailed Results</h4>
        <table>
          <thead>
            <tr>
              <th className="text-[1vw]">Option</th>
              <th className="text-[1vw]">Count</th>
              <th className="text-[1vw]">Percentage</th>
            </tr>
          </thead>
          <tbody>
            {options.map((option, index) => {
              const key = typeof option === 'object' ? option.id : option;
              const text = typeof option === 'object' ? option.choice_text : option;
              return (
                <tr key={index}>
                  <td className="text-[1vw]">{text}</td>
                  <td className="text-[1vw]">{optionCounts[key] || 0}</td>
                  <td className="text-[1vw]">{percentages[key] || 0}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResultsChart;
